// TDS2 SHIM — replace with @salesforce/trailhead-ui AgentPlanningWorklist when TDS2 lands.
// Mirrors the prototype's `AgentPlanningWorklist` (ChatComponentsPage.tsx + UnitBuildAgent.tsx).
// Renders an inline "Agent To-do List" card with per-step state icons. Once every step has
// completed (or `forceCollapsible` is on), the title becomes a toggle button + chevron and
// the body can be collapsed.
// Source: /prototype/chat-components → "Agent To-do List".
import React from 'react';
import PropTypes from 'prop-types';

const STEP_STATES = ['completed', 'in_progress', 'error', 'retrying', 'disabled'];

function CheckIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function ListChecksIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="3 17 5 19 9 15" />
      <polyline points="3 7 5 9 9 5" />
      <line x1="13" y1="6" x2="21" y2="6" />
      <line x1="13" y1="12" x2="21" y2="12" />
      <line x1="13" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function RocketIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
      <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

// Derives the header status pill from the step states, matching the prototype's
// "Agent To-do List / Publishing" cards: an errored step shows "Error", an
// all-complete list shows "Done", and anything still running shows "In progress".
// Returns null when nothing has started yet (all disabled) so an untouched card
// stays quiet.
function derivePillState(steps) {
  if (!steps || steps.length === 0) return null;
  if (steps.some((s) => s.state === 'error')) return 'error';
  // A step being auto-corrected wins over "in progress" so the header reads
  // "Fixing…" (not "Error") — the failure is transient, not terminal.
  if (steps.some((s) => s.state === 'retrying')) return 'retrying';
  if (steps.every((s) => s.state === 'completed')) return 'done';
  if (steps.some((s) => s.state === 'in_progress' || s.state === 'completed')) {
    return 'in_progress';
  }
  return null;
}

const PILL_LABELS = {
  in_progress: 'In progress',
  retrying: 'Fixing…',
  error: 'Error',
  done: 'Done',
};

function StatusPill({ state }) {
  if (!state) return null;
  return (
    <span
      className={'tds2-agentforce-planning-worklist__pill tds2-agentforce-planning-worklist__pill--' + state}
    >
      {state === 'in_progress' || state === 'retrying' ? (
        <span className="tds2-agentforce-planning__spinner" aria-hidden="true" />
      ) : null}
      {PILL_LABELS[state]}
    </span>
  );
}

StatusPill.propTypes = {
  state: PropTypes.oneOf(['in_progress', 'retrying', 'error', 'done']),
};

function StepIcon({ state }) {
  if (state === 'completed') {
    return (
      <span
        className="tds2-agentforce-planning__icon tds2-agentforce-planning__icon--completed"
        aria-hidden="true"
      >
        <CheckIcon />
      </span>
    );
  }
  if (state === 'in_progress') {
    return <span className="tds2-agentforce-planning__spinner" aria-hidden="true" />;
  }
  if (state === 'retrying') {
    return (
      <span
        className="tds2-agentforce-planning__spinner tds2-agentforce-planning__spinner--retrying"
        aria-hidden="true"
      />
    );
  }
  if (state === 'error') {
    return (
      <span
        className="tds2-agentforce-planning__icon tds2-agentforce-planning__icon--error"
        aria-hidden="true"
      >
        <AlertIcon />
      </span>
    );
  }
  return <span className="tds2-agentforce-planning__dot" aria-hidden="true" />;
}

StepIcon.propTypes = {
  state: PropTypes.oneOf(STEP_STATES).isRequired,
};

export default class PlanningWorklist extends React.Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
    steps: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        state: PropTypes.oneOf(STEP_STATES).isRequired,
        // Optional sub-line under the step (active-step message, or the
        // "fixing & retrying" summary with the error). Rendered when present.
        detail: PropTypes.string,
      })
    ).isRequired,
    ariaLabel: PropTypes.string,
    // Start collapsed when the worklist is collapsible (i.e. all steps complete
    // OR `forceCollapsible` is on). Ignored mid-stream while steps are still
    // running — the user wants to see live progress without an extra click.
    defaultCollapsed: PropTypes.bool,
    // Allow collapsing even when steps aren't all complete. Off by default;
    // mid-stream worklists are useless when collapsed because the user can't
    // see what's happening.
    forceCollapsible: PropTypes.bool,
  };

  static defaultProps = {
    defaultCollapsed: false,
    forceCollapsible: false,
  };

  constructor(props) {
    super(props);
    const isCollapsible = props.forceCollapsible || PlanningWorklist.allComplete(props.steps);
    this.state = { collapsed: props.defaultCollapsed && isCollapsible };
  }

  static allComplete(steps) {
    return steps.length > 0 && steps.every((s) => s.state === 'completed');
  }

  toggle = () => {
    this.setState((prev) => ({ collapsed: !prev.collapsed }));
  };

  render() {
    const { title, steps, ariaLabel, forceCollapsible } = this.props;
    const { collapsed } = this.state;

    const completedCount = steps.filter((s) => s.state === 'completed').length;
    const pillState = derivePillState(steps);
    const isPublishLike = /publish|deploy|activat/i.test(title || '');
    const TitleIcon = isPublishLike ? RocketIcon : ListChecksIcon;
    const allComplete = PlanningWorklist.allComplete(steps);
    const isCollapsible = forceCollapsible || allComplete;
    const isCollapsed = collapsed && isCollapsible;

    const rootClass = 'tds2-agentforce-planning-worklist' +
      (isCollapsed ? ' tds2-agentforce-planning-worklist--collapsed' : '');

    const titleInner = (
      <React.Fragment>
        <span className="tds2-agentforce-planning-worklist__title-icon" aria-hidden="true">
          <TitleIcon />
        </span>
        <span className="tds2-agentforce-planning-worklist__label">{title}</span>
        <span className="tds2-agentforce-planning-worklist__count">
          ({completedCount}/{steps.length})
        </span>
        <StatusPill state={pillState} />
        {isCollapsible ? (
          <span
            className={
              'tds2-agentforce-planning-worklist__chevron' +
              (isCollapsed ? ' tds2-agentforce-planning-worklist__chevron--collapsed' : '')
            }
          >
            <ChevronIcon />
          </span>
        ) : null}
      </React.Fragment>
    );

    return (
      <div
        className={rootClass}
        data-tds2-shim="AgentPlanningWorklist"
        aria-label={ariaLabel || title}
      >
        {isCollapsible ? (
          <button
            type="button"
            className="tds2-agentforce-planning-worklist__title tds2-agentforce-planning-worklist__title--button"
            onClick={this.toggle}
            aria-expanded={!isCollapsed}
          >
            {titleInner}
          </button>
        ) : (
          <div className="tds2-agentforce-planning-worklist__title">{titleInner}</div>
        )}
        {isCollapsed ? null : steps.map((step, idx) => (
          <div
            key={step.label + '-' + idx}
            className={
              'tds2-agentforce-planning-worklist__item' +
              (step.detail ? ' tds2-agentforce-planning-worklist__item--with-detail' : '')
            }
          >
            <StepIcon state={step.state} />
            <span className="tds2-agentforce-planning-worklist__item-text">
              <span
                className={
                  'tds2-agentforce-planning__step-label tds2-agentforce-planning__step-label--' +
                  step.state
                }
              >
                {step.label}
              </span>
              {step.detail ? (
                <span
                  className={
                    'tds2-agentforce-planning__step-detail tds2-agentforce-planning__step-detail--' +
                    step.state
                  }
                >
                  {step.detail}
                </span>
              ) : null}
            </span>
          </div>
        ))}
      </div>
    );
  }
}
